import React, { useState, useEffect, useCallback } from 'react';
import { Card, CardContent } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import {
  ChartBar, Lightning, TrendUp, TrendDown, Minus, Warning, ShieldCheck,
  CurrencyCircleDollar, ArrowRight, Clock, Sparkle, Brain, Target,
  ChartLineUp, CaretRight, Spinner
} from '@phosphor-icons/react';
import { Toaster, toast } from 'sonner';
import { analyticsApi } from '../../services/api';

const focusOptions = [
  { value: 'general', label: 'General Overview', icon: Brain, desc: 'Comprehensive analysis' },
  { value: 'denial_prediction', label: 'Denial Prediction', icon: Warning, desc: 'Risk patterns & prevention' },
  { value: 'revenue_forecast', label: 'Revenue Forecast', icon: TrendUp, desc: '3-month projection' },
  { value: 'anomaly_detection', label: 'Anomaly Detection', icon: Lightning, desc: 'Outliers & red flags' },
  { value: 'settlement_prediction', label: 'Settlement Prediction', icon: Clock, desc: 'Payer timelines' },
];

const severityColors = {
  info: 'bg-sky-50 border-sky-200 text-sky-700',
  warning: 'bg-amber-50 border-amber-200 text-amber-700',
  critical: 'bg-rose-50 border-rose-200 text-rose-700',
};

const statusBadge = {
  good: 'bg-emerald-100 text-emerald-700',
  warning: 'bg-amber-100 text-amber-700',
  critical: 'bg-rose-100 text-rose-700',
};

const trendIcon = { up: TrendUp, down: TrendDown, stable: Minus };

const formatCurrency = (v) => `INR ${(v || 0).toLocaleString('en-IN', {maximumFractionDigits: 0})}`;

export default function AIAnalyticsPage() {
  const [data, setData] = useState(null);
  const [insights, setInsights] = useState(null);
  const [loading, setLoading] = useState(true);
  const [aiLoading, setAiLoading] = useState(false);
  const [focus, setFocus] = useState('general');

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    setLoading(true);
    try {
      const res = await analyticsApi.getData();
      setData(res.data);
    } catch (e) { toast.error('Failed to load analytics data'); }
    setLoading(false);
  };

  const runAI = async (f) => {
    setAiLoading(true);
    setFocus(f || focus);
    try {
      const res = await analyticsApi.getInsights(f || focus);
      setInsights(res.data?.insights || null);
      toast.success('AI analysis complete');
    } catch (e) {
      toast.error(e.response?.data?.detail || 'AI analysis failed');
    }
    setAiLoading(false);
  };

  if (loading) return (
    <div className="flex items-center justify-center h-96">
      <Spinner className="h-8 w-8 text-indigo-500 animate-spin" />
    </div>
  );

  return (
    <div className="space-y-6" data-testid="ai-analytics-page">
      <Toaster position="top-right" richColors />

      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-xl font-bold font-['Outfit'] text-slate-800 flex items-center gap-2">
            <Brain className="h-6 w-6 text-indigo-500" weight="duotone" /> AI Analytics
          </h1>
          <p className="text-sm text-slate-500 mt-0.5">AI-powered claim insights & predictions</p>
        </div>
      </div>

      {/* KPI Cards */}
      {data && (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3" data-testid="kpi-cards">
          <KPICard label="Total Claims" value={data.total_claims} icon={ChartBar} color="indigo" />
          <KPICard label="Collection Rate" value={`${data.collection_rate}%`} icon={Target} color="emerald" />
          <KPICard label="Denial Rate" value={`${data.denial_rate}%`} icon={Warning} color="rose" />
          <KPICard label="Avg Processing" value={`${data.avg_processing_days}d`} icon={Clock} color="amber" />
        </div>
      )}

      {/* AI Focus Selector */}
      <Card className="bg-gradient-to-br from-slate-900 to-indigo-950 border-0 text-white">
        <CardContent className="pt-5 pb-5">
          <div className="flex items-center gap-2 mb-4">
            <Sparkle className="h-5 w-5 text-amber-400" weight="fill" />
            <h2 className="text-sm font-semibold font-['Outfit']">AI Analysis Engine</h2>
            <Badge className="bg-indigo-500/30 text-indigo-200 border-0 text-[9px]">GPT-5.2</Badge>
          </div>
          <div className="grid grid-cols-2 sm:grid-cols-5 gap-2 mb-4">
            {focusOptions.map(opt => {
              const Icon = opt.icon;
              const active = focus === opt.value;
              return (
                <button key={opt.value} onClick={() => setFocus(opt.value)}
                  className={`p-3 rounded-xl border text-left transition-all ${active ? 'bg-indigo-500/30 border-indigo-400' : 'bg-white/5 border-white/10 hover:bg-white/10'}`}
                  data-testid={`focus-${opt.value}`}>
                  <Icon className={`h-4 w-4 mb-1 ${active ? 'text-indigo-300' : 'text-slate-400'}`} />
                  <p className={`text-xs font-medium ${active ? 'text-white' : 'text-slate-300'}`}>{opt.label}</p>
                  <p className="text-[10px] text-slate-500 mt-0.5">{opt.desc}</p>
                </button>
              );
            })}
          </div>
          <Button onClick={() => runAI(focus)} disabled={aiLoading}
            className="bg-gradient-to-r from-indigo-500 to-violet-600 hover:from-indigo-600 hover:to-violet-700 text-white gap-2 w-full sm:w-auto"
            data-testid="run-ai-btn">
            {aiLoading ? <><Spinner className="h-4 w-4 animate-spin" /> Analyzing...</> : <><Sparkle className="h-4 w-4" weight="fill" /> Run AI Analysis</>}
          </Button>
        </CardContent>
      </Card>

      {/* AI Results */}
      {insights && (
        <div className="space-y-4" data-testid="ai-results">
          {/* Summary */}
          <Card className="bg-white border border-slate-200">
            <CardContent className="pt-5">
              <div className="flex items-center justify-between mb-3">
                <h3 className="text-sm font-semibold font-['Outfit'] text-slate-800 flex items-center gap-2">
                  <Brain className="h-4 w-4 text-indigo-500" /> Executive Summary
                </h3>
                <Badge className={`text-[10px] border-0 ${insights.risk_score > 7 ? 'bg-rose-100 text-rose-700' : insights.risk_score > 4 ? 'bg-amber-100 text-amber-700' : 'bg-emerald-100 text-emerald-700'}`}>
                  Risk Score: {insights.risk_score}/10
                </Badge>
              </div>
              <p className="text-sm text-slate-600 leading-relaxed">{insights.summary}</p>
            </CardContent>
          </Card>

          {/* Key Metrics */}
          {insights.key_metrics?.length > 0 && (
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
              {insights.key_metrics.map((m, i) => {
                const TIcon = trendIcon[m.trend] || Minus;
                return (
                  <Card key={i} className="bg-white border border-slate-200">
                    <CardContent className="pt-4 pb-4">
                      <div className="flex items-center justify-between mb-1">
                        <p className="text-[10px] text-slate-500 font-medium uppercase tracking-wide">{m.metric}</p>
                        <Badge className={`text-[9px] border-0 ${statusBadge[m.status] || statusBadge.good}`}>{m.status}</Badge>
                      </div>
                      <p className="text-lg font-bold text-slate-800">{m.value}</p>
                      <div className="flex items-center gap-1 mt-1">
                        <TIcon className={`h-3 w-3 ${m.trend === 'up' ? 'text-emerald-500' : m.trend === 'down' ? 'text-rose-500' : 'text-slate-400'}`} />
                        <span className="text-[10px] text-slate-400">{m.trend}</span>
                      </div>
                    </CardContent>
                  </Card>
                );
              })}
            </div>
          )}

          {/* Insights */}
          {insights.insights?.length > 0 && (
            <Card className="bg-white border border-slate-200">
              <CardContent className="pt-5">
                <h3 className="text-sm font-semibold font-['Outfit'] text-slate-800 mb-3 flex items-center gap-2">
                  <Lightning className="h-4 w-4 text-amber-500" /> Key Insights
                </h3>
                <div className="space-y-3">
                  {insights.insights.map((ins, i) => (
                    <div key={i} className={`p-3 rounded-lg border ${severityColors[ins.severity] || severityColors.info}`}>
                      <div className="flex items-center gap-2 mb-1">
                        <Badge className={`text-[9px] border-0 ${ins.severity === 'critical' ? 'bg-rose-200 text-rose-800' : ins.severity === 'warning' ? 'bg-amber-200 text-amber-800' : 'bg-sky-200 text-sky-800'}`}>
                          {ins.severity?.toUpperCase()}
                        </Badge>
                        <span className="text-xs font-semibold">{ins.title}</span>
                      </div>
                      <p className="text-xs leading-relaxed">{ins.description}</p>
                      {ins.action && (
                        <p className="text-xs mt-2 flex items-center gap-1 font-medium">
                          <ArrowRight className="h-3 w-3" /> {ins.action}
                        </p>
                      )}
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          )}

          {/* Predictions */}
          {insights.predictions?.length > 0 && (
            <Card className="bg-white border border-slate-200">
              <CardContent className="pt-5">
                <h3 className="text-sm font-semibold font-['Outfit'] text-slate-800 mb-3 flex items-center gap-2">
                  <ChartLineUp className="h-4 w-4 text-violet-500" /> Predictions
                </h3>
                <div className="space-y-2">
                  {insights.predictions.map((p, i) => (
                    <div key={i} className="flex items-start gap-3 p-3 rounded-lg bg-slate-50 border border-slate-100">
                      <Target className="h-4 w-4 text-violet-500 mt-0.5 shrink-0" />
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2">
                          <span className="text-xs font-semibold text-slate-800">{p.title}</span>
                          <Badge className={`text-[9px] border-0 ${p.confidence === 'high' ? 'bg-emerald-100 text-emerald-700' : p.confidence === 'medium' ? 'bg-amber-100 text-amber-700' : 'bg-slate-200 text-slate-600'}`}>
                            {p.confidence} confidence
                          </Badge>
                        </div>
                        <p className="text-xs text-slate-500 mt-1">{p.detail}</p>
                      </div>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          )}

          {/* Recommendations */}
          {insights.recommendations?.length > 0 && (
            <Card className="bg-white border border-slate-200">
              <CardContent className="pt-5">
                <h3 className="text-sm font-semibold font-['Outfit'] text-slate-800 mb-3 flex items-center gap-2">
                  <ShieldCheck className="h-4 w-4 text-emerald-500" /> Recommendations
                </h3>
                <div className="space-y-2">
                  {insights.recommendations.map((r, i) => (
                    <div key={i} className="flex items-start gap-3 p-3 rounded-lg border border-slate-100 hover:bg-slate-50 transition-colors">
                      <div className={`h-6 w-6 rounded-full flex items-center justify-center text-xs font-bold shrink-0 ${r.priority === 'high' ? 'bg-rose-100 text-rose-700' : r.priority === 'medium' ? 'bg-amber-100 text-amber-700' : 'bg-slate-100 text-slate-600'}`}>
                        {i + 1}
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className="text-xs font-semibold text-slate-800">{r.title}</p>
                        <p className="text-xs text-slate-500 mt-0.5">{r.impact}</p>
                        <div className="flex gap-2 mt-1.5">
                          <Badge className={`text-[9px] border-0 ${r.priority === 'high' ? 'bg-rose-100 text-rose-700' : r.priority === 'medium' ? 'bg-amber-100 text-amber-700' : 'bg-slate-200 text-slate-600'}`}>
                            {r.priority} priority
                          </Badge>
                          <Badge className="text-[9px] border-0 bg-slate-100 text-slate-600">{r.effort} effort</Badge>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          )}
        </div>
      )}

      {/* Data Summary Cards */}
      {data && (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {/* Payer Performance */}
          <Card className="bg-white border border-slate-200">
            <CardContent className="pt-5">
              <h3 className="text-sm font-semibold font-['Outfit'] text-slate-800 mb-3">Payer Performance</h3>
              <div className="space-y-2 max-h-64 overflow-y-auto">
                {data.payer_performance?.slice(0, 8).map((p, i) => (
                  <div key={i} className="flex items-center justify-between p-2 rounded bg-slate-50">
                    <div className="flex-1 min-w-0">
                      <p className="text-xs font-medium text-slate-700 truncate">{p.payer}</p>
                      <p className="text-[10px] text-slate-400">{p.claims} claims</p>
                    </div>
                    <div className="text-right">
                      <p className="text-xs font-semibold text-slate-800">{formatCurrency(p.billed)}</p>
                      <Badge className={`text-[9px] border-0 ${p.denial_rate > 20 ? 'bg-rose-100 text-rose-700' : p.denial_rate > 10 ? 'bg-amber-100 text-amber-700' : 'bg-emerald-100 text-emerald-700'}`}>
                        {p.denial_rate}% denied
                      </Badge>
                    </div>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>

          {/* Aging */}
          <Card className="bg-white border border-slate-200">
            <CardContent className="pt-5">
              <h3 className="text-sm font-semibold font-['Outfit'] text-slate-800 mb-3">Claim Aging</h3>
              <div className="space-y-3">
                {[
                  { label: '0-30 days', value: data.aging_buckets?.['0_30'] || 0, color: 'bg-emerald-500' },
                  { label: '31-60 days', value: data.aging_buckets?.['31_60'] || 0, color: 'bg-amber-500' },
                  { label: '61-90 days', value: data.aging_buckets?.['61_90'] || 0, color: 'bg-orange-500' },
                  { label: '90+ days', value: data.aging_buckets?.['90_plus'] || 0, color: 'bg-rose-500' },
                ].map((b, i) => {
                  const total = (data.aging_buckets?.['0_30'] || 0) + (data.aging_buckets?.['31_60'] || 0) + (data.aging_buckets?.['61_90'] || 0) + (data.aging_buckets?.['90_plus'] || 0);
                  const pct = total > 0 ? (b.value / total * 100) : 0;
                  return (
                    <div key={i}>
                      <div className="flex justify-between text-xs mb-1">
                        <span className="text-slate-600">{b.label}</span>
                        <span className="font-semibold text-slate-800">{b.value}</span>
                      </div>
                      <div className="h-2 bg-slate-100 rounded-full overflow-hidden">
                        <div className={`h-full ${b.color} rounded-full transition-all`} style={{ width: `${pct}%` }} />
                      </div>
                    </div>
                  );
                })}
              </div>
            </CardContent>
          </Card>
        </div>
      )}
    </div>
  );
}

function KPICard({ label, value, icon: Icon, color }) {
  const colors = {
    indigo: 'bg-indigo-50 text-indigo-600',
    emerald: 'bg-emerald-50 text-emerald-600',
    rose: 'bg-rose-50 text-rose-600',
    amber: 'bg-amber-50 text-amber-600',
  };
  return (
    <Card className="bg-white border border-slate-200">
      <CardContent className="pt-4 pb-4">
        <div className={`inline-flex p-2 rounded-lg ${colors[color]} mb-2`}>
          <Icon className="h-4 w-4" weight="duotone" />
        </div>
        <p className="text-[10px] text-slate-500 font-medium uppercase tracking-wide">{label}</p>
        <p className="text-lg font-bold text-slate-800 mt-0.5">{value}</p>
      </CardContent>
    </Card>
  );
}
